import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemText,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Add, Delete, Cloud, Smartphone } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import {
  forgetPasskeyOnThisDevice,
  isDismissal,
  passkeyService,
  passkeySupported,
  type Passkey,
} from '../../services/passkey.service';

function describe(k: Passkey): string {
  const used = k.last_used_at
    ? `last used ${new Date(k.last_used_at).toLocaleDateString()}`
    : 'never used';
  return `Added ${new Date(k.created_at).toLocaleDateString()} · ${used}`;
}

export default function PasskeysPanel() {
  const { enqueueSnackbar } = useSnackbar();
  const [keys, setKeys] = useState<Passkey[] | null>(null);
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [removing, setRemoving] = useState<Passkey | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);

  // Kept apart from the empty case: told they have none when the request merely
  // failed, a person concludes theirs was revoked.
  const load = useCallback(() => {
    setLoadFailed(false);
    setKeys(null);
    passkeyService.list().then(setKeys).catch(() => setLoadFailed(true));
  }, []);

  useEffect(() => { load(); }, [load]);

  const add = async () => {
    setBusy(true);
    try {
      const created = await passkeyService.enroll(name.trim() || 'This device');
      setKeys((prev) => [created, ...(prev ?? [])]);
      setAdding(false);
      setName('');
      enqueueSnackbar('Passkey added', { variant: 'success' });
    } catch (err) {
      if (!isDismissal(err)) {
        enqueueSnackbar('Could not add that passkey', { variant: 'error' });
      }
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!removing) return;
    setBusy(true);
    try {
      await passkeyService.remove(removing.id);
      const left = (keys ?? []).filter((p) => p.id !== removing.id);
      setKeys(left);
      if (left.length === 0) forgetPasskeyOnThisDevice();
      setRemoving(null);
    } catch {
      enqueueSnackbar('Could not remove that passkey', { variant: 'error' });
    } finally {
      setBusy(false);
    }
  };

  if (!passkeySupported()) {
    return (
      <Alert severity="info">
        This browser cannot use passkeys. Sign in with Google or your password.
      </Alert>
    );
  }

  return (
    <Box>
      <Typography variant="subtitle1" fontWeight={600}>
        Passkeys
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Sign in with your fingerprint, face or device PIN instead of a password. The key stays on
        your device — we only hold the public half, so it cannot be phished or leaked.
      </Typography>

      {loadFailed && (
        <Alert
          severity="error"
          action={<Button color="inherit" size="small" onClick={load}>Retry</Button>}
        >
          Could not load your passkeys.
        </Alert>
      )}

      {keys === null && !loadFailed && <CircularProgress size={20} />}

      {keys !== null && keys.length === 0 && !adding && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          No passkeys yet.
        </Typography>
      )}

      {keys !== null && keys.length > 0 && (
        <List dense sx={{ mb: 1 }}>
          {keys.map((k) => (
            <ListItem
              key={k.id}
              disableGutters
              secondaryAction={
                <IconButton edge="end" size="small" onClick={() => setRemoving(k)} aria-label={`Remove ${k.name}`}>
                  <Delete fontSize="small" />
                </IconButton>
              }
            >
              <Tooltip title={k.backed_up ? 'Synced to your other devices' : 'This device only'}>
                <Box sx={{ display: 'flex', alignItems: 'center', mr: 1.5, color: 'text.secondary' }}>
                  {k.backed_up ? <Cloud fontSize="small" /> : <Smartphone fontSize="small" />}
                </Box>
              </Tooltip>
              <ListItemText primary={k.name} secondary={describe(k)} />
            </ListItem>
          ))}
        </List>
      )}

      {adding ? (
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <TextField
            size="small"
            autoFocus
            label="Name it"
            placeholder="MacBook Touch ID"
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={busy}
          />
          <Button variant="contained" onClick={add} disabled={busy}>
            {busy ? <CircularProgress size={20} /> : 'Continue'}
          </Button>
          <Button onClick={() => setAdding(false)} disabled={busy}>
            Cancel
          </Button>
        </Box>
      ) : (
        !loadFailed && (
          <Button startIcon={<Add />} onClick={() => setAdding(true)} disabled={keys === null}>
            Add a passkey
          </Button>
        )
      )}

      <Dialog open={!!removing} onClose={() => !busy && setRemoving(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Remove &ldquo;{removing?.name}&rdquo;?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            You will not be able to sign in with it again. The key itself stays on your device
            until you delete it there too.
          </Typography>
          {keys?.length === 1 && (
            <Alert severity="info" sx={{ mt: 2 }}>
              This is your only passkey. You can still sign in with Google or your password.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRemoving(null)} disabled={busy}>Cancel</Button>
          <Button onClick={remove} color="error" variant="contained" disabled={busy}>
            {busy ? <CircularProgress size={20} /> : 'Remove'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
